import * as React from "react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDateTime } from "@/lib/date-time"
import type { HrisRun } from "@/features/integrations/hris/hrisApi"

const SHOWN = 10

function RunStatus({ run }: { run: HrisRun }) {
  const t = useBookingT()
  switch (run.status) {
    case "ok":
      return <Badge variant="green">{t("hris_run_ok", "Done")}</Badge>
    case "blocked":
      return <Badge variant="warning">{t("hris_run_blocked", "Waiting for confirmation")}</Badge>
    case "failed":
      return <Badge variant="destructive">{t("hris_run_failed", "Failed")}</Badge>
    default:
      return <Badge variant="secondary">{t("hris_run_in_progress", "In progress")}</Badge>
  }
}

/** The latest runs: when, what kind, and what they changed. */
export function HrisHistoryCard({ runs }: { runs: HrisRun[] }) {
  const t = useBookingT()
  if (runs.length === 0) return null

  const summary = (run: HrisRun): string => {
    const p = run.stats?.planned
    if (!p) return run.error ?? "—"
    return [
      t("hris_sum_new", "New: {count}").replace("{count}", String(p.create)),
      t("hris_sum_updated", "Updated: {count}").replace("{count}", String(p.update + p.link + p.restore)),
      t("hris_sum_archived", "Archived: {count}").replace("{count}", String(p.archive)),
      t("hris_sum_attention", "Need attention: {count}").replace("{count}", String(p.conflict)),
    ].join(" · ")
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <h2 className="text-lg font-semibold text-neutral-900">{t("hris_history_title", "History")}</h2>
        <div className="overflow-x-auto rounded-md border border-neutral-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("hris_col_when", "When")}</TableHead>
                <TableHead>{t("hris_col_kind", "Kind")}</TableHead>
                <TableHead>{t("hris_col_status", "Status")}</TableHead>
                <TableHead>{t("hris_col_summary", "Summary")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.slice(0, SHOWN).map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="whitespace-nowrap text-sm text-neutral-900">{formatUserDateTime(run.startedAt)}</TableCell>
                  <TableCell className="text-sm text-neutral-800">
                    {run.kind === "preview"
                      ? t("hris_kind_preview", "Preview")
                      : run.trigger === "cron"
                        ? t("hris_kind_nightly", "Nightly sync")
                        : t("hris_kind_manual", "Manual sync")}
                  </TableCell>
                  <TableCell><RunStatus run={run} /></TableCell>
                  <TableCell className="text-sm text-neutral-800">{summary(run)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
